import { useLazyQuery } from "@apollo/client/react";
import { CloseOutlined, FileAddOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import { cx } from "@linaria/core";
import { Button, Flex, Input, Segmented, Space, Table, Tag, Typography } from "antd";
import { type KeyboardEvent, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { UnreachableCaseError } from "ts-essentials";
import { GenericIngredientDetails } from "../../../components/GenericIngredientDetails.tsx";
import { NutritionInfo } from "../../../components/NutritionInfo.tsx";
import { TableEmptyState } from "../../../components/TableEmptyState.tsx";
import { genericIngredientDisplayName } from "../../../utils/genericIngredient.ts";
import {
  GetIngredientsDocument,
  GetRecipesDocument,
  type IngredientFragment,
  IngredientType,
  type RecipeFragment,
  SearchGenericIngredientsDocument,
  type SearchGenericIngredientsQuery,
} from "../../../gql/generated/graphqlSchema.tsx";
import { useDebouncedSearch } from "../../../hooks/useDebouncedSearch.ts";
import { clickable, title } from "../../../styles/shared.styles.ts";
import {
  chevronCell,
  container,
  footerRow,
  leftPane,
  rightPane,
  searchRow,
  splitArea,
} from "../../../styles/splitPaneSearch.styles.ts";

const pageSize = 10;

type GenericIngredient = SearchGenericIngredientsQuery["searchGenericIngredients"]["ingredients"][number];

export type SearchTab = "ingredients" | "recipes" | "generics";

export type SearchItem = {
  readonly id: string;
  readonly name: string;
  readonly nutritionProfile: IngredientFragment["nutritionProfile"] | null;
  readonly ingredientType: IngredientType;
};

type DisplayItem =
  | { readonly kind: "ingredient"; readonly data: IngredientFragment }
  | { readonly kind: "recipe"; readonly data: RecipeFragment }
  | { readonly kind: "generic"; readonly data: GenericIngredient };

const toSearchItem = (item: DisplayItem): SearchItem | null => {
  switch (item.kind) {
    case "ingredient":
      return {
        id: item.data.id,
        name: item.data.name,
        nutritionProfile: item.data.nutritionProfile,
        ingredientType: IngredientType.Ingredient,
      };
    case "recipe":
      return {
        id: item.data.id,
        name: item.data.name,
        nutritionProfile: item.data.nutritionProfile ?? null,
        ingredientType: IngredientType.Recipe,
      };
    case "generic":
      return null;
    default:
      throw new UnreachableCaseError(item);
  }
};

const rowName = (item: DisplayItem, language: string): string => {
  switch (item.kind) {
    case "ingredient":
      return item.data.name;
    case "recipe":
      return item.data.name;
    case "generic":
      return genericIngredientDisplayName(item.data, language);
    default:
      throw new UnreachableCaseError(item);
  }
};

export const IngredientSearch = ({
  actionIcon,
  actionLabel,
  defaultTab = "ingredients",
  genericActionLabel = undefined,
  onCancel,
  onCreateFromGeneric = undefined,
  onCreateIngredient = undefined,
  onSelect,
}: {
  readonly actionIcon: ReactNode;
  readonly actionLabel: string;
  readonly defaultTab?: SearchTab;
  readonly genericActionLabel?: string;
  readonly onCancel: () => void;
  readonly onCreateFromGeneric?: (generic: GenericIngredient) => void;
  readonly onCreateIngredient?: () => void;
  readonly onSelect: (item: SearchItem) => void;
}) => {
  const { t, i18n } = useTranslation();

  const [searchIngredients, { data: ingredientData, previousData: ingredientPrev, loading: loadingIngredients }] =
    useLazyQuery(GetIngredientsDocument);
  const [searchRecipes, { data: recipeData, previousData: recipePrev, loading: loadingRecipes }] =
    useLazyQuery(GetRecipesDocument);
  const [searchGenerics, { data: genericData, previousData: genericPrev, loading: loadingGenerics }] = useLazyQuery(
    SearchGenericIngredientsDocument,
  );

  const [tab, setTab] = useState<SearchTab>(defaultTab);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const ingredientResult = ingredientData?.ingredients ?? ingredientPrev?.ingredients;
  const recipeResult = recipeData?.recipes ?? recipePrev?.recipes;
  const genericResult = genericData?.searchGenericIngredients ?? genericPrev?.searchGenericIngredients;

  const ingredientTotal = ingredientResult?.total ?? 0;
  const recipeTotal = recipeResult?.total ?? 0;
  const genericTotal = genericResult?.total ?? 0;

  const results: DisplayItem[] =
    tab === "ingredients"
      ? (ingredientResult?.ingredients ?? []).map((data) => ({ kind: "ingredient", data }))
      : tab === "recipes"
        ? (recipeResult?.recipes ?? []).map((data) => ({ kind: "recipe", data }))
        : (genericResult?.ingredients ?? []).map((data) => ({ kind: "generic", data }));

  const loading = tab === "ingredients" ? loadingIngredients : tab === "recipes" ? loadingRecipes : loadingGenerics;
  const total = tab === "ingredients" ? ingredientTotal : tab === "recipes" ? recipeTotal : genericTotal;

  const effectiveIndex =
    selectedIndex != null && selectedIndex < results.length ? selectedIndex : results.length > 0 ? 0 : null;
  const selected = effectiveIndex != null ? results[effectiveIndex] : null;

  const fireSearch = useCallback(
    (query: string, offset: number) => {
      const pagination = { offset, limit: pageSize };
      void searchIngredients({ variables: { search: query, pagination } });
      void searchRecipes({ variables: { search: query, pagination } });
      void searchGenerics({
        variables: {
          query,
          offset,
          limit: pageSize,
          language: i18n.language,
        },
      });
    },
    [i18n.language, searchGenerics, searchIngredients, searchRecipes],
  );

  useEffect(() => {
    fireSearch("", 0);
  }, [fireSearch]);

  const { debounce: debouncedFireSearch } = useDebouncedSearch((value) => fireSearch(value, 0));

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
    setSelectedIndex(null);
    debouncedFireSearch(value);
  };

  const handleTabChange = (newTab: SearchTab) => {
    setTab(newTab);
    setPage(1);
    setSelectedIndex(null);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setSelectedIndex(null);
    const offset = (newPage - 1) * pageSize;
    // Only refetch the active tab's query — others keep previousData.
    if (tab === "ingredients") {
      void searchIngredients({ variables: { search: searchQuery, pagination: { offset, limit: pageSize } } });
    } else if (tab === "recipes") {
      void searchRecipes({ variables: { search: searchQuery, pagination: { offset, limit: pageSize } } });
    } else {
      void searchGenerics({
        variables: {
          query: searchQuery,
          offset,
          limit: pageSize,
          language: i18n.language,
        },
      });
    }
  };

  const scrollToIndex = (index: number) => {
    const row = listRef.current?.querySelectorAll(".ant-table-row")[index];
    row?.scrollIntoView({ block: "nearest" });
  };

  const confirmItem = useCallback(
    (item: DisplayItem) => {
      if (item.kind === "generic") {
        if (onCreateFromGeneric != null) onCreateFromGeneric(item.data);
        return;
      }
      const searchItem = toSearchItem(item);
      if (searchItem != null) onSelect(searchItem);
    },
    [onCreateFromGeneric, onSelect],
  );

  const handleConfirm = useCallback(() => {
    if (selected == null) return;
    confirmItem(selected);
  }, [confirmItem, selected]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (results.length === 0) return;
    const current = effectiveIndex ?? 0;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = Math.min(current + 1, results.length - 1);
      setSelectedIndex(next);
      scrollToIndex(next);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const next = Math.max(current - 1, 0);
      setSelectedIndex(next);
      scrollToIndex(next);
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleConfirm();
    }
  };

  const confirmDisabled = selected == null || (selected.kind === "generic" && onCreateFromGeneric == null);
  const confirmLabel = selected?.kind === "generic" && genericActionLabel != null ? genericActionLabel : actionLabel;

  return (
    <div className={container}>
      <div className={searchRow}>
        <Input
          allowClear
          autoFocus
          onChange={(e) => handleSearchChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("recipes.searchIngredient")}
          prefix={<SearchOutlined />}
          size="large"
          value={searchQuery}
        />
        <Segmented
          block
          onChange={(v) => handleTabChange(v as SearchTab)}
          options={[
            {
              value: "ingredients",
              label: `${t("recipes.ingredientTypes.IngredientRecipeIngredient")} (${ingredientTotal})`,
            },
            {
              value: "recipes",
              label: `${t("recipes.ingredientTypes.SubRecipeRecipeIngredient")} (${recipeTotal})`,
            },
            {
              value: "generics",
              label: `${t("recipes.ingredientTypes.GenericIngredient")} (${genericTotal})`,
            },
          ]}
          value={tab}
        />
      </div>

      <div className={splitArea}>
        <div className={leftPane} ref={listRef}>
          <Table<DisplayItem>
            columns={[
              {
                title: t("recipes.ingredientName"),
                key: "name",
                ellipsis: { showTitle: true },
                render: (_, record) => rowName(record, i18n.language),
              },
              {
                key: "chevron",
                width: 32,
                className: chevronCell,
                render: () => <RightOutlined />,
              },
            ]}
            dataSource={results}
            loading={loading}
            locale={{
              emptyText: (
                <TableEmptyState
                  emptyDescription={t("recipes.searchNoResults", { query: searchQuery })}
                  loading={loading}
                />
              ),
            }}
            onChange={(pag) => {
              const newPage = pag.current ?? 1;
              if (newPage !== page) handlePageChange(newPage);
            }}
            onRow={(record, index) => ({
              onClick: () => setSelectedIndex(index ?? null),
              onDoubleClick: () => {
                if (record.kind === "generic" && onCreateFromGeneric == null) return;
                confirmItem(record);
              },
              className: cx(clickable, index === effectiveIndex && "ant-table-row-selected"),
            })}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: false,
            }}
            rowKey={(row) => `${row.kind}:${row.data.id}`}
            showHeader={false}
            size="small"
            tableLayout="fixed"
          />
        </div>

        {selected != null ? (
          <div className={rightPane}>
            <DetailsPanel item={selected} />
          </div>
        ) : null}
      </div>

      <div className={footerRow} style={{ justifyContent: "space-between" }}>
        {onCreateIngredient != null && tab === "ingredients" ? (
          <Button color="primary" icon={<FileAddOutlined />} onClick={onCreateIngredient} variant="outlined">
            {t("ingredients.createIngredient")}
          </Button>
        ) : (
          <span />
        )}
        <Space wrap>
          <Button icon={<CloseOutlined />} onClick={onCancel}>
            {t("common.cancel")}
          </Button>
          <Button color="primary" disabled={confirmDisabled} icon={actionIcon} onClick={handleConfirm} variant="solid">
            {confirmLabel}
          </Button>
        </Space>
      </div>
    </div>
  );
};

// eslint-disable-next-line react/no-multi-comp
const DetailsPanel = ({ item }: { readonly item: DisplayItem }) => {
  switch (item.kind) {
    case "ingredient":
      return <IngredientDetails ingredient={item.data} />;
    case "recipe":
      return <RecipeDetails recipe={item.data} />;
    case "generic":
      return <GenericIngredientDetails ingredient={item.data} />;
    default:
      throw new UnreachableCaseError(item);
  }
};

// eslint-disable-next-line react/no-multi-comp
const IngredientDetails = ({ ingredient }: { readonly ingredient: IngredientFragment }) => (
  <Flex gap="middle" vertical>
    <Space align="center" size="small" wrap>
      <Typography.Title className={title} level={5}>
        {ingredient.name}
      </Typography.Title>
      {ingredient.brand != null && ingredient.brand.length > 0 ? <Tag>{ingredient.brand}</Tag> : null}
    </Space>
    {ingredient.description != null && ingredient.description.length > 0 ? (
      <Typography.Paragraph type="secondary">{ingredient.description}</Typography.Paragraph>
    ) : null}
    <NutritionInfo nutritionProfile={ingredient.nutritionProfile} />
  </Flex>
);

// eslint-disable-next-line react/no-multi-comp
const RecipeDetails = ({ recipe }: { readonly recipe: RecipeFragment }) => (
  <Flex gap="middle" vertical>
    <Typography.Title className={title} level={5}>
      {recipe.name}
    </Typography.Title>
    {recipe.description != null && recipe.description.length > 0 ? (
      <Typography.Paragraph type="secondary">{recipe.description}</Typography.Paragraph>
    ) : null}
    {recipe.nutritionProfile != null ? <NutritionInfo nutritionProfile={recipe.nutritionProfile} /> : null}
  </Flex>
);
